import React from 'react';
import { StyleSheet, View } from 'react-native';
import { AppText } from '../AppText';
import Colors from '../../constants/Colors';
import { Window } from '../../types/Site';
import { formatChipDayLabel, formatChipTimeRange } from './siteFormatting';

const styles = StyleSheet.create({
  chip: {
    flexDirection: 'column',
    alignItems: 'center',
    paddingVertical: 5,
    paddingHorizontal: 10,
    borderRadius: 8,
  },
  dayLabel: {
    fontSize: 13,
    fontWeight: '700',
    lineHeight: 17,
  },
  timeLabel: {
    fontSize: 13,
    fontWeight: '600',
    lineHeight: 17,
  },
  soonestWalkIn: { backgroundColor: Colors.darkGreen },
  soonestWalkInText: { color: Colors.white },
  reservation: {
    backgroundColor: Colors.siteListReservationBg,
    borderWidth: 1,
    borderColor: Colors.siteListReservationBorder,
  },
  reservationText: { color: Colors.siteListReservationText },
  preRegistration: {
    backgroundColor: Colors.siteListPreRegistrationBg,
    borderWidth: 1,
    borderColor: Colors.siteListPreRegistrationBorder,
  },
  preRegistrationText: { color: Colors.siteListPreRegistrationText },
  neutral: { backgroundColor: Colors.lightGrayBackground },
  neutralDayText: { color: Colors.veryDarkGray },
  neutralTimeText: { color: Colors.mediumGray },
});

export type DateChipVariant = 'soonestWalkIn' | 'reservation' | 'preRegistration' | 'neutral';

export interface DateChipProps {
  window: Window;
  variant: DateChipVariant;
  now: Date;
  todayLabel: string;
  tomorrowLabel: string;
  timezone?: string;
  locale?: string;
}

const CONTAINER_STYLE_BY_VARIANT: Record<DateChipVariant, object> = {
  soonestWalkIn: styles.soonestWalkIn,
  reservation: styles.reservation,
  preRegistration: styles.preRegistration,
  neutral: styles.neutral,
};

const TEXT_STYLE_BY_VARIANT: Record<DateChipVariant, object> = {
  soonestWalkIn: styles.soonestWalkInText,
  reservation: styles.reservationText,
  preRegistration: styles.preRegistrationText,
  neutral: styles.neutralDayText,
};

export function DateChip({
  window,
  variant,
  now,
  todayLabel,
  tomorrowLabel,
  timezone,
  locale,
}: DateChipProps) {
  const dayLabel = formatChipDayLabel(window.opensAt, now, todayLabel, tomorrowLabel, timezone, locale);
  const timeLabel = formatChipTimeRange(window, timezone, locale);

  const containerStyle = CONTAINER_STYLE_BY_VARIANT[variant];
  const dayTextStyle = TEXT_STYLE_BY_VARIANT[variant];
  // Neutral is the only variant with a separately-muted time label -- every other variant uses
  // one solid text color for both lines.
  const timeTextStyle = variant === 'neutral' ? styles.neutralTimeText : TEXT_STYLE_BY_VARIANT[variant];

  return (
    <View style={[styles.chip, containerStyle]} testID={`date-chip-${window.occurrenceId}`}>
      <AppText style={[styles.dayLabel, dayTextStyle]}>{dayLabel}</AppText>
      <AppText style={[styles.timeLabel, timeTextStyle]}>{timeLabel}</AppText>
    </View>
  );
}
